import { useCallback, useEffect, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

import type { NormalizedAddress } from '../utils';
import { enrichAddressWithGeocodeResults, extractPrimaryAddress, normalizeAddress } from '../utils';

type GeocodingResult = {
  position: google.maps.LatLngLiteral | null;
  placeId: string;
  enrichedAddress: NormalizedAddress | null;
  isLoading: boolean;
  shouldShow: boolean;
};

export const useAddressGeocoding = (
  address?: string | null,
  countryCode?: string | null,
  onAddressEnriched?: (address: NormalizedAddress) => void,
): GeocodingResult => {
  const geocodingLib = useMapsLibrary('geocoding');
  const placesLib = useMapsLibrary('places');

  const [position, setPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [placeId, setPlaceId] = useState<string>('');
  const [enrichedAddress, setEnrichedAddress] = useState<NormalizedAddress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(true);
  const [lastProcessedAddress, setLastProcessedAddress] = useState<string>('');

  const tryTextSearch = useCallback(
    async (addr: string): Promise<google.maps.GeocoderResult | null> => {
      if (!placesLib) return null;

      try {
        const service = new placesLib.PlacesService(document.createElement('div'));

        const result = await new Promise<google.maps.places.PlaceResult[] | null>(resolve => {
          service.textSearch({ query: addr }, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
              resolve(results);
            } else {
              resolve(null);
            }
          });
        });

        const place = result?.at(0);

        if (place?.geometry?.location && place.formatted_address) {
          return {
            formatted_address: place.formatted_address,
            geometry: {
              location: place.geometry.location,
              location_type: google.maps.GeocoderLocationType.GEOMETRIC_CENTER,
              viewport: place.geometry.viewport || new google.maps.LatLngBounds(),
              bounds: place.geometry.viewport,
            },
            place_id: place.place_id || '',
            types: place.types || ['establishment'],
            address_components: [],
          };
        }
      } catch (error) {
        // Silent error handling
      }

      return null;
    },
    [placesLib],
  );

  const tryPlacesSearch = useCallback(
    async (addr: string): Promise<google.maps.GeocoderResult | null> => {
      if (!placesLib) return null;

      try {
        const service = new placesLib.PlacesService(document.createElement('div'));

        const result = await new Promise<google.maps.places.PlaceResult[] | null>(resolve => {
          service.findPlaceFromQuery(
            {
              query: addr,
              fields: ['formatted_address', 'geometry', 'place_id', 'name', 'types'],
            },
            (results, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                resolve(results);
              } else {
                resolve(null);
              }
            },
          );
        });

        const place = result?.at(0);

        if (place?.geometry?.location && place.formatted_address) {
          return {
            formatted_address: place.formatted_address,
            geometry: {
              location: place.geometry.location,
              location_type: google.maps.GeocoderLocationType.GEOMETRIC_CENTER,
              viewport: place.geometry.viewport || new google.maps.LatLngBounds(),
              bounds: place.geometry.viewport,
            },
            place_id: place.place_id || '',
            types: place.types || ['establishment'],
            address_components: [],
          };
        }
      } catch (error) {
        // Silent error handling
      }

      return null;
    },
    [placesLib],
  );

  const tryGeocodeAddress = useCallback(
    async (addr: string): Promise<google.maps.GeocoderResult | null> => {
      if (!geocodingLib) return null;

      const geocoder = new geocodingLib.Geocoder();

      const expandAbbreviations = (address: string): string => {
        return address
          .replace(/\bblvd\b/gi, 'Boulevard')
          .replace(/\bst\b/gi, 'Street')
          .replace(/\bave\b/gi, 'Avenue')
          .replace(/\brd\b/gi, 'Road')
          .replace(/\bdr\b/gi, 'Drive');
      };

      try {
        const strategies = [
          { address: addr },
          countryCode ? { address: addr, region: countryCode } : null,
          { address: expandAbbreviations(addr) },
        ].filter(Boolean);

        for (const strategy of strategies) {
          const results = await new Promise<google.maps.GeocoderResult[] | null>(resolve => {
            void geocoder.geocode(strategy!, (results, status) => {
              if (status === google.maps.GeocoderStatus.OK && results?.length) {
                resolve(results);
              } else {
                resolve(null);
              }
            });
          });

          if (results?.[0]) {
            return results[0];
          }
        }
      } catch (error) {
        // Silent error handling
      }

      return null;
    },
    [geocodingLib, countryCode],
  );

  const geocodeAddress = useCallback(
    async (addressToGeocode: string) => {
      setIsLoading(true);

      const cleanAddress = extractPrimaryAddress(addressToGeocode);

      try {
        let geocodeResult = await tryTextSearch(cleanAddress);

        if (!geocodeResult) {
          geocodeResult = await tryPlacesSearch(cleanAddress);
        }

        if (!geocodeResult) {
          geocodeResult = await tryGeocodeAddress(cleanAddress);
        }

        if (!geocodeResult) {
          setShouldShow(false);
          setIsLoading(false);

          return;
        }

        const location = geocodeResult.geometry.location;
        const initialPosition = { lat: location.lat(), lng: location.lng() };

        setPosition(initialPosition);
        setPlaceId(geocodeResult.place_id || '');

        const initialAddressDetails = normalizeAddress(cleanAddress, countryCode);
        const enriched = enrichAddressWithGeocodeResults(initialAddressDetails, geocodeResult);

        setEnrichedAddress(enriched);
        onAddressEnriched?.(enriched);

        if (geocodeResult.geometry.location_type === google.maps.GeocoderLocationType.APPROXIMATE) {
          setShouldShow(false);
          setIsLoading(false);

          return;
        }

        setShouldShow(true);
      } catch (error) {
        setShouldShow(false);
      }

      setIsLoading(false);
    },
    [tryTextSearch, tryPlacesSearch, tryGeocodeAddress, countryCode, onAddressEnriched],
  );

  useEffect(() => {
    if (!geocodingLib || !placesLib || !address) {
      setShouldShow(false);

      return;
    }

    if (lastProcessedAddress === address) {
      return;
    }

    setPosition(null);
    setPlaceId('');
    setEnrichedAddress(null);
    setShouldShow(true);
    setLastProcessedAddress(address);

    void geocodeAddress(address);
  }, [geocodingLib, placesLib, address, lastProcessedAddress, geocodeAddress]);

  return {
    position,
    placeId,
    enrichedAddress,
    isLoading,
    shouldShow,
  };
};
